/**
 * @file EntityModalProvider.js
 * @version 1.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-20
 * @modified 2026-10-19
 * @description One place that knows how to open the right modal for any entity
 *              the system displays. Call sites use <EntityLink type="..." id={...}>
 *              and never import a modal or hold modal state of their own.
 *
 *              Why a registry rather than wiring each call site: the modals do
 *              NOT share a prop contract. Most take an id under their own name
 *              (invoiceId, billId, orderId...), VehicleModal takes a whole
 *              object plus isOpen, and PurchaseOrderModal takes the *Xero* id
 *              rather than the local one. Spreading that knowledge across every
 *              table in the app is how the "passed po.id, got a silent 404" bug
 *              keeps recurring. It lives here once instead.
 */

import React, { createContext, useContext, useState, useCallback, Suspense, lazy } from 'react';
import { useSlideOutButton } from './SlideOutButton';
import { startEdit, editable } from '../utils/editHandoff';

// Modals are lazy so adding entity links everywhere doesn't drag every modal
// into the initial bundle.
const InvoiceModal        = lazy(() => import('./InvoiceModal'));
const PurchaseOrderModal  = lazy(() => import('./PurchaseOrderModal'));
const CustomerOrderModal  = lazy(() => import('./CustomerOrderModal'));
const ContactModal        = lazy(() => import('./ContactModal'));
const BillModal           = lazy(() => import('./BillModal'));
const InventoryItemModal  = lazy(() => import('./InventoryItemModal'));
const VehicleModal        = lazy(() => import('./VehicleModal'));
const DriverModal         = lazy(() => import('./DriverModal'));
const SalesRepModal       = lazy(() => import('./SalesRepModal'));
const PaymentModal        = lazy(() => import('./PaymentModal'));

/**
 * Registry. Each entry maps an opaque `id` onto that modal's own props.
 *
 * `idKind` documents what the id must BE, because getting this wrong fails
 * silently rather than loudly:
 *   'db'     — local integer primary key
 *   'xero'   — Xero id string, including TEMP-/LOCAL- placeholders
 *   'object' — the modal wants the whole record, not an id
 */
export const ENTITY_REGISTRY = {
  invoice: {
    label: 'Invoice',
    idKind: 'db',
    component: InvoiceModal,
    searchMenu: 'search-invoices',
    referenceOf: (r) => r?.invoice_no,
    toProps: (id, onClose) => ({ invoiceId: id, onClose })
  },
  // A credit note is an invoice row with is_credit — same modal, same id space.
  credit_note: {
    label: 'Credit note',
    idKind: 'db',
    component: InvoiceModal,
    searchMenu: 'search-invoices',
    referenceOf: (r) => r?.invoice_no,
    toProps: (id, onClose) => ({ invoiceId: id, onClose })
  },
  purchase_order: {
    label: 'Purchase order',
    // PurchaseOrderModal queries WHERE xero_purchase_order_id = $1. Passing the
    // local integer id returns "not found" with no error — see CLAUDE.md.
    idKind: 'xero',
    component: PurchaseOrderModal,
    searchMenu: 'search-purchase-orders',
    referenceOf: (r) => r?.purchaseOrderNumber || r?.purchase_order_number,
    toProps: (id, onClose) => ({ purchaseOrderId: id, onClose })
  },
  customer_order: {
    label: 'Sales order',
    idKind: 'db',
    component: CustomerOrderModal,
    searchMenu: 'search-customer-orders',
    referenceOf: (r) => r?.order_number,
    toProps: (id, onClose) => ({ orderId: id, onClose })
  },
  contact: {
    label: 'Contact',
    idKind: 'db',
    component: ContactModal,
    toProps: (id, onClose) => ({ contactId: id, onClose })
  },
  bill: {
    label: 'Bill',
    idKind: 'db',
    component: BillModal,
    searchMenu: 'search-bills',
    referenceOf: (r) => r?.supplier_reference || r?.bill_number,
    toProps: (id, onClose) => ({ billId: id, onClose })
  },
  inventory_item: {
    label: 'Item',
    idKind: 'db',
    component: InventoryItemModal,
    toProps: (id, onClose) => ({ itemId: id, onClose })
  },
  vehicle: {
    label: 'Vehicle',
    // VehicleModal renders from a record rather than fetching one.
    idKind: 'object',
    component: VehicleModal,
    toProps: (record, onClose) => ({ isOpen: true, vehicle: record, onClose })
  },
  driver: {
    label: 'Driver',
    idKind: 'db',
    component: DriverModal,
    toProps: (id, onClose) => ({ driverId: id, onClose })
  },
  sales_rep: {
    label: 'Rep',
    idKind: 'db',
    component: SalesRepModal,
    toProps: (id, onClose) => ({ repId: id, onClose })
  },
  payment: {
    label: 'Payment',
    idKind: 'db',
    component: PaymentModal,
    toProps: (id, onClose) => ({ paymentId: id, onClose })
  }
};

const EntityModalContext = createContext(null);

/**
 * openEntity(type, id) from anywhere beneath the provider.
 *
 * openEntity(type, id, { onClose }) also tells the opener when that modal is
 * closed — so a panel inside another modal can refresh what it shows after the
 * user changed something in the one it opened (LinkedDocuments does this).
 */
export function useEntityModal() {
  const ctx = useContext(EntityModalContext);
  // Returning a no-op rather than throwing means an EntityLink rendered outside
  // the provider degrades to plain text instead of white-screening the app.
  return ctx || { openEntity: () => {}, available: false };
}

export function EntityModalProvider({ children, onNavigate }) {
  // A stack, not a single slot: opening a PO from inside an invoice and then a
  // contact from inside that PO should return you back down the chain rather
  // than dumping you out to the page.
  const [stack, setStack] = useState([]);

  // 1.3.0 (2026-10-15, Cam — "some view modals are not calling it"): this
  // provider wraps the whole signed-in app, so holding the hook here keeps its
  // shared observer on for every screen and every window opened from anywhere
  // (lazy modals included): a slide-out button that appears later than its own
  // component's measure — data loaded, a portal a cycle late — is still sized.
  // Each component still calls the hook where its buttons mount (CLAUDE.md).
  useSlideOutButton([]);

  const openEntity = useCallback((type, id, options = {}) => {
    if (!ENTITY_REGISTRY[type]) {
      console.warn(`[EntityModal] No modal registered for entity type "${type}"`);
      return;
    }
    if (id === null || id === undefined || id === '') return;
    setStack(prev => [...prev, { type, id, key: `${type}:${id}:${prev.length}`, onClose: options.onClose }]);
  }, []);

  // The callback runs outside the state updater: an updater must stay pure,
  // and StrictMode calls it twice.
  const top = stack[stack.length - 1];
  const closeTop = useCallback(() => {
    const closing = top;
    setStack(prev => prev.slice(0, -1));
    if (typeof closing?.onClose === 'function') closing.onClose();
  }, [top]);

  // Only the top of the stack renders — the ones beneath stay mounted-in-memory
  // only as state, so we never stack overlays on top of each other.
  const entry = top ? ENTITY_REGISTRY[top.type] : null;
  const Modal = entry ? entry.component : null;

  return (
    <EntityModalContext.Provider value={{ openEntity, available: true }}>
      {children}
      {Modal && (
        <Suspense fallback={null}>
          {/* onNavigate reaches every modal in the stack. A modal opened from
              an EntityLink has no route of its own to leave by, so anything
              that wants to send the user to another screen — the item modal's
              cross-search, today — would otherwise need its own path threaded
              through every call site. Modals that do not use it ignore it. */}
          {/* "Find in …" is not passed from here any more: each modal works it
              out itself (hooks/useOpenInSearch), which also knows when the user
              is already on that list and leaves the button off. */}
          {/* Edit (Cam, 19 Oct 2026): the order, PO and bill windows show their
              Edit when given onEdit — the hand-off their search screens use
              (utils/editHandoff). The invoice window has its own Edit. The
              whole stack closes: the screen behind is about to change. */}
          <Modal
            key={top.key}
            {...entry.toProps(top.id, closeTop)}
            onNavigate={onNavigate}
            {...(editable(top.type) && top.type !== 'invoice' && top.type !== 'credit_note' && typeof onNavigate === 'function'
              ? { onEdit: (id) => { if (startEdit(top.type, id ?? top.id, onNavigate)) setStack([]); } }
              : {})}
          />
        </Suspense>
      )}
    </EntityModalContext.Provider>
  );
}

export default EntityModalProvider;
